<template>
  <v-card class="mt-4" prepend-icon="mdi-chart-bar-stacked" title="Visao geral da turma">
    <v-card-text>
      <v-row class="mb-4" align="center">
        <v-col cols="12" md="4">
          <v-select
            v-model="selectedTurmaId"
            :items="turmas"
            item-title="display"
            item-value="id"
            label="Turma"
            :loading="loadingTurmas"
            hide-details
            no-data-text="Nenhuma turma cadastrada"
          />
        </v-col>
        <v-col cols="12" md="8">
          <v-chip color="primary" variant="tonal">
            {{ selectedTurmaLabel }} - {{ filteredStudentList.length }} aluno(s)
          </v-chip>
        </v-col>
      </v-row>

      <v-alert
        v-if="selectedTurmaId && !filteredStudentList.length"
        type="info"
        variant="tonal"
        class="mb-4"
      >
        Nenhum aluno com metricas encontrado para a turma selecionada.
      </v-alert>

      <v-row>
        <v-col cols="12" md="6">
          <v-card elevation="2" class="mb-4">
            <v-toolbar color="primary" elevation="1">
              <v-spacer />Media geral da turma em cada semestre<v-spacer />
            </v-toolbar>
            <v-card-text class="chart-card-text">
              <Bar :data="chartDataSemester" :options="chartOptionsSemester" />
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="6">
          <v-card elevation="2" class="mb-4">
            <v-toolbar color="secondary" elevation="1">
              <v-spacer />Media geral da turma do Teste de Progresso<v-spacer />
            </v-toolbar>
            <v-card-text class="chart-card-text">
              <Bar :data="chartDataProgress" :options="chartOptionsProgress" />
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-card elevation="2" class="mb-4">
            <v-toolbar color="primary" elevation="1">
              <v-spacer />Comparativo: Media do semestre x Teste de Progresso correspondente<v-spacer />
            </v-toolbar>
            <v-card-text class="chart-card-text">
              <Bar :data="chartDataComparison" :options="chartOptionsComparison" />
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-card elevation="2" class="mb-4">
            <v-toolbar color="secondary" elevation="1">
              <v-spacer />Media da turma por disciplina no periodo selecionado<v-spacer />
            </v-toolbar>
            <v-card-text>
              <v-alert
                v-if="!disciplineRecords.length"
                type="info"
                variant="tonal"
                class="mb-4"
              >
                Nenhuma nota de disciplina disponivel para montar o grafico da turma.
              </v-alert>
              <template v-else>
                <v-row class="mb-4" align="center">
                  <v-col cols="12" md="4">
                    <v-select
                      v-model="selectedDisciplinePeriod"
                      :items="availableDisciplinePeriods"
                      label="Periodo"
                      density="comfortable"
                      hide-details
                    />
                  </v-col>
                  <v-col cols="12" md="8">
                    <v-chip color="secondary" variant="tonal">
                      {{ selectedTurmaLabel }} - {{ selectedDisciplinePeriod || "Periodo" }}
                    </v-chip>
                  </v-col>
                </v-row>

                <v-alert
                  v-if="selectedDisciplinePeriod && !classDisciplineAverages.length"
                  type="info"
                  variant="tonal"
                  class="mb-4"
                >
                  Nenhuma media de disciplina encontrada para a turma neste periodo.
                </v-alert>

                <div v-else class="chart-card-text">
                  <Bar
                    :data="chartDataClassDisciplines"
                    :options="chartOptionsClassDisciplines"
                  />
                </div>
              </template>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import { Bar } from "vue-chartjs";
import UserDataService from "@/assets/js/services/UserDataService";
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const periods = ["1P", "2P", "3P", "4P", "5P", "6P", "7P", "8P", "9P", "10P", "11P", "12P"];

export default {
  name: "TurmaEstatisticas",
  components: { Bar },
  props: {
    students: {
      type: Array,
      default: () => [],
    },
    disciplineRecords: {
      type: Array,
      default: () => [],
    },
    simulatedMode: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["turma-selecionada"],
  data: () => ({
    turmas: [],
    turmaAlunos: [],
    selectedTurmaId: null,
    selectedDisciplinePeriod: "",
    loadingTurmas: false,
    loadingAlunos: false,
  }),
  computed: {
    studentList() {
      return this.students;
    },
    selectedTurmaLabel() {
      const turma = this.turmas.find((item) => item.id === this.selectedTurmaId);
      return turma?.codigo || "Turma";
    },
    filteredStudentList() {
      if (this.simulatedMode || !this.selectedTurmaId) {
        return this.studentList;
      }
      if (!this.turmaAlunos.length) {
        // Turma selecionada mas sem alunos matriculados em TurmaAluno: o
        // resultado correto e "sem dados", nunca cair para "todos os alunos"
        // (que e o que acontecia antes e vazava dados de outras turmas).
        return [];
      }
      const studentsByName = new Map(
        this.studentList.map((student) => [
          this.normalizeStudentName(student.name),
          student,
        ])
      );
      const studentsByRa = new Map(
        this.studentList
          .filter((student) => student.ra)
          .map((student) => [String(student.ra).trim(), student])
      );
      return this.turmaAlunos.map((aluno) => {
        const name = aluno.nome || aluno.email || `Aluno ${aluno.aluno_id || aluno.id}`;
        const ra = String(aluno.ra || "").trim();
        const matchedStudent =
          (ra && studentsByRa.get(ra)) ||
          studentsByName.get(this.normalizeStudentName(name));
        return matchedStudent || {
          id: aluno.aluno_id || aluno.id,
          ra,
          name,
          medSeries: [],
          tpSeries: [],
          values: [],
          seriesLabels: [],
        };
      });
    },
    semesterAverages() {
      return periods.map((period, index) => ({
        label: period,
        med: this.averageSeriesValue("medSeries", index),
        tp: this.averageSeriesValue("tpSeries", index),
      }));
    },
    chartDataSemester() {
      return {
        labels: this.semesterAverages.map((item) => item.label),
        datasets: [
          {
            label: "Media da turma",
            backgroundColor: "#3C6E8F",
            borderRadius: 10,
            data: this.semesterAverages.map((item) => item.med),
          },
        ],
      };
    },
    chartDataProgress() {
      return {
        labels: this.semesterAverages.map((item) => item.label),
        datasets: [
          {
            label: "Media do Teste de Progresso",
            backgroundColor: "#6E3C8F",
            borderRadius: 10,
            data: this.semesterAverages.map((item) => item.tp),
          },
        ],
      };
    },
    chartDataComparison() {
      return {
        labels: this.semesterAverages.map((item) => item.label),
        datasets: [
          {
            label: "Media do semestre",
            backgroundColor: "#3C6E8F",
            borderRadius: 10,
            data: this.semesterAverages.map((item) => item.med),
          },
          {
            label: "Media do Teste de Progresso",
            backgroundColor: "#6E3C8F",
            borderRadius: 10,
            data: this.semesterAverages.map((item) => item.tp),
          },
        ],
      };
    },
    chartOptionsSemester() {
      return this.buildChartOptions(false);
    },
    chartOptionsProgress() {
      return this.buildChartOptions(false);
    },
    chartOptionsComparison() {
      return this.buildChartOptions(true);
    },
    availableDisciplinePeriods() {
      const periods = this.filteredClassDisciplineRecords
        .map((record) => record.period)
        .filter(Boolean);
      return [...new Set(periods)].sort(
        (a, b) => this.periodSortValue(a) - this.periodSortValue(b)
      );
    },
    filteredClassDisciplineRecords() {
      const selectedTurma = this.selectedTurmaLabel;
      const allowedStudents = this.filteredStudentIdentitySet;
      return this.disciplineRecords.filter((record) => {
        if (allowedStudents.size) {
          return this.recordBelongsToFilteredClass(record, allowedStudents);
        }
        // Fallback for manually imported spreadsheets where student membership
        // is unavailable and the turma column already matches the app turma.
        if (!selectedTurma || selectedTurma === "Turma") return true;
        return (
          String(record.turma || "").trim().toLowerCase() ===
          String(selectedTurma).trim().toLowerCase()
        );
      });
    },
    filteredStudentIdentitySet() {
      const identities = new Set();
      this.filteredStudentList.forEach((student) => {
        const ra = String(student.ra || "").trim();
        const name = this.normalizeStudentName(student.name);
        if (ra) identities.add(`ra:${ra}`);
        if (name) identities.add(`name:${name}`);
      });
      return identities;
    },
    classDisciplineRowsForPeriod() {
      if (!this.selectedDisciplinePeriod) return [];
      return this.filteredClassDisciplineRecords.filter(
        (record) => record.period === this.selectedDisciplinePeriod
      );
    },
    classDisciplineAverages() {
      const byDiscipline = new Map();
      this.classDisciplineRowsForPeriod.forEach((record) => {
        if (this.shouldIgnoreDisciplineRecord(record)) return;
        const discipline = String(record.discipline || "").trim();
        if (!discipline) return;
        const value = Number(record.average);
        if (!Number.isFinite(value)) return;
        if (!byDiscipline.has(discipline)) byDiscipline.set(discipline, []);
        byDiscipline.get(discipline).push(value);
      });
      return [...byDiscipline.entries()]
        .map(([discipline, values]) => ({
          discipline,
          average: Number(
            (values.reduce((sum, value) => sum + value, 0) / values.length).toFixed(2)
          ),
        }))
        .sort((a, b) => a.discipline.localeCompare(b.discipline, "pt-BR"));
    },
    chartDataClassDisciplines() {
      return {
        labels: this.classDisciplineAverages.map((item) => item.discipline),
        datasets: [
          {
            label: "Media da turma",
            backgroundColor: "#2E7D6F",
            borderRadius: 8,
            data: this.classDisciplineAverages.map((item) => item.average),
          },
        ],
      };
    },
    chartOptionsClassDisciplines() {
      return {
        ...this.buildChartOptions(false),
        plugins: {
          legend: { display: false },
          title: { display: false },
          tooltip: {
            callbacks: {
              label: (ctx) => `Media da turma: ${this.formatValue(ctx.raw)}`,
            },
          },
        },
      };
    },
  },
  watch: {
    selectedTurmaId(value) {
      this.selectedDisciplinePeriod = "";
      this.loadTurmaAlunos(value);
      const turma = this.turmas.find((item) => item.id === value);
      // Avisa o componente pai pra ele buscar o dashboard ja filtrado por
      // turma no backend, em vez de carregar o banco inteiro e filtrar so
      // aqui no navegador.
      this.$emit("turma-selecionada", turma?.codigo || null);
    },
    availableDisciplinePeriods(newPeriods) {
      if (newPeriods.length && !newPeriods.includes(this.selectedDisciplinePeriod)) {
        this.selectedDisciplinePeriod = newPeriods[0];
      }
      if (!newPeriods.length) {
        this.selectedDisciplinePeriod = "";
      }
    },
  },
  mounted() {
    this.loadTurmas();
  },
  methods: {
    normalizeStudentName(value) {
      return String(value || "")
        .trim()
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/\s+/g, " ");
    },
    averageSeriesValue(seriesKey, index) {
      const values = this.filteredStudentList
        .map((student) => {
          if (Array.isArray(student[seriesKey])) {
            return student[seriesKey][index];
          }
          const prefix = seriesKey === "tpSeries" ? "TP" : "MED";
          const labelIndex = (student.seriesLabels || []).findIndex((label) => {
            const parts = String(label || "").split("_");
            return parts[0]?.toUpperCase() === prefix && parts[1] === periods[index];
          });
          return labelIndex >= 0 ? student.values?.[labelIndex] : null;
        })
        .filter((value) => value !== null && value !== undefined);
      return values.length
        ? Number((values.reduce((sum, value) => sum + Number(value), 0) / values.length).toFixed(2))
        : null;
    },
    buildChartOptions(showLegend) {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: showLegend, position: "top" },
          title: { display: false },
        },
        scales: {
          y: {
            beginAtZero: true,
            max: 10,
            ticks: { stepSize: 1 },
          },
          x: {
            grid: { display: false },
          },
        },
      };
    },
    periodSortValue(period) {
      const match = String(period || "").match(/\d+/);
      return match ? Number(match[0]) : 999;
    },
    formatValue(value) {
      return Number(value || 0).toLocaleString("pt-BR", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 2,
      });
    },
    normalizeDisciplineName(value) {
      return String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim()
        .toLowerCase();
    },
    shouldIgnoreDisciplineRecord(record) {
      return (
        this.normalizeDisciplineName(record.discipline).includes("programa integrador") &&
        Number(record.average || 0) === 0
      );
    },
    recordBelongsToFilteredClass(record, allowedStudents) {
      const ra = String(record.ra || "").trim();
      const name = this.normalizeStudentName(record.studentName);
      return (
        (ra && allowedStudents.has(`ra:${ra}`)) ||
        (name && allowedStudents.has(`name:${name}`))
      );
    },
    async loadTurmas() {
      this.loadingTurmas = true;
      try {
        const response = await UserDataService.listTurmas();
        this.turmas = (response.data || []).map((turma) => ({
          ...turma,
          display: turma.codigo,
        }));
        if (this.turmas.length && !this.selectedTurmaId) {
          this.selectedTurmaId = this.turmas[0].id;
        }
      } catch (_error) {
        this.turmas = [];
      } finally {
        this.loadingTurmas = false;
      }
    },
    async loadTurmaAlunos(turmaId) {
      if (!turmaId) {
        this.turmaAlunos = [];
        return;
      }
      this.loadingAlunos = true;
      try {
        const response = await UserDataService.listTurmaAlunos({ id: turmaId });
        this.turmaAlunos = response.data || [];
      } catch (_error) {
        this.turmaAlunos = [];
      } finally {
        this.loadingAlunos = false;
      }
    },
  },
};
</script>

<style scoped>
.chart-card-text {
  min-height: 320px;
}
</style>
